import React, { useState, useCallback, useEffect, useMemo } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import {
 Row,
 Col,
 Card,
 Button,
 Form,
 Table,
 Badge,
 Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import { generateSafetyReport, getSafetyStats } from "../../../services/safetyService";

/* Constants */
const SEVERITY_COLORS = {
 Low: "success",
 Medium: "warning",
 High: "danger",
 Critical: "dark",
};

const STATUS_COLORS = {
 Draft: "secondary",
 Submitted: "warning",
 "Under Investigation": "info",
 Resolved: "success",
 Closed: "dark",
};

const CA_STATUS_COLORS = {
 Open: "warning",
 "In Progress": "info",
 Completed: "success",
 Overdue: "danger",
};

const INCIDENT_TYPES = [
 "Near Miss", "First Aid", "Medical Treatment", "Lost Time Injury", "Fatality",
 "Property Damage", "Environmental", "Fire", "Electrical Hazard", "Chemical Spill",
 "Fall from Height", "Struck By Object", "Caught In/Between",
 "Safety Observation", "Unsafe Act", "Unsafe Condition",
 "Toolbox Talk", "Safety Audit", "Compliance Check", "Other",
];

/* Component */
const SafetyReport = () => {
 const [filters, setFilters] = usePersistentFilters("safety_report_filters", {
 project: "",
 workOrder: "",
 fromDate: "",
 toDate: "",
 status: "",
 incidentType: "",
 severity: "",
 });

 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [stats, setStats] = useState(null);
 const [reportData, setReportData] = useState(null);
 const [loading, setLoading] = useState(false);
 const [statsLoading, setStatsLoading] = useState(true);

 /* Load reference data */
 useEffect(() => {
 const loadRefs = async () => {
 try {
 const [pRes] = await Promise.all([api.get("/projects?limit=500")]);
 setProjects(pRes.data?.data || []);
 } catch (_) {}
 };
 loadRefs();
 }, []);

 useEffect(() => {
    const params = filters.project ? { project: filters.project, limit: 1000 } : { limit: 1000 };
    api
      .get("/work-orders", { params })
      .then((res) => setWorkOrders(res.data?.data || []))
      .catch(() => {});
  }, [filters.project]);

 /* Load stats on mount */
 useEffect(() => {
 const loadStats = async () => {
 try {
 const res = await getSafetyStats();
 if (res.data.success) setStats(res.data.data);
 } catch (_) {}
 finally { setStatsLoading(false); }
 };
 loadStats();
 }, []);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "project") {
        if (value && prev.workOrder) {
          const currentWo = workOrders.find((w) => String(w._id) === String(prev.workOrder));
          const currentWoProjId = currentWo?.project?._id || currentWo?.project;
          if (currentWoProjId && String(currentWoProjId) !== String(value)) {
            next.workOrder = "";
          }
        }
      } else if (name === "workOrder") {
        if (value) {
          const selectedWo = workOrders.find((w) => String(w._id) === String(value));
          const autoProjId = selectedWo?.project?._id || selectedWo?.project;
          if (autoProjId) {
            next.project = String(autoProjId);
          }
        }
      }
      return next;
    });
  };

  const filteredWorkOrders = useMemo(() => {
    if (!filters.project) return workOrders;
    return workOrders.filter((wo) => {
      const pid = wo.project?._id || wo.project;
      return String(pid) === String(filters.project);
    });
  }, [workOrders, filters.project]);

 const handleGenerate = async () => {
 setLoading(true);
 setReportData(null);
 try {
 const params = {};
 if (filters.project) params.project = filters.project;
 if (filters.workOrder) params.workOrder = filters.workOrder;
 if (filters.status) params.status = filters.status;
 if (filters.incidentType) params.incidentType = filters.incidentType;
 if (filters.severity) params.severity = filters.severity;
 if (filters.fromDate) params.fromDate = filters.fromDate;
 if (filters.toDate) params.toDate = filters.toDate;

 const [reportRes, statsRes] = await Promise.all([
 generateSafetyReport(params),
 getSafetyStats(params),
 ]);

 if (reportRes.data.success) {
 setReportData(reportRes.data.data);
 }
 if (statsRes.data.success) {
 setStats(statsRes.data.data);
 }
 } catch (_) {
 toast.error("Failed to generate report");
 } finally {
 setLoading(false);
 }
 };

 const fmt = (date) =>
 date ? new Date(date).toLocaleDateString("en-IN") : "—";

 /* Count open CAs across records */
 const countOpenCAs = (records) =>
 records.reduce(
 (acc, r) =>
 acc +
 (r.correctiveActions || []).filter((ca) =>
 ["Open", "In Progress", "Overdue"].includes(ca.status)
 ).length,
 0
 );

 return (
 <>
 <PageTitle activeMenu="Safety Report" motherMenu="Safety" />

 <div className="mb-3">
 <Link to="/safety" className="btn btn-outline-secondary btn-sm">
 <i className="fas fa-arrow-left me-1" /> Back to Safety
 </Link>
 </div>

 {/* Overall Stats */}
 {!statsLoading && stats && (
 <Row className="mb-4">
 {[
 { label: "Total Incidents", value: stats.total, color: "primary", icon: "fa-shield-alt" },
 { label: "Open", value: stats.open, color: "warning", icon: "fa-exclamation-circle" },
 { label: "Resolved", value: stats.resolved, color: "success", icon: "fa-check-circle" },
 { label: "Critical", value: stats.critical, color: "danger", icon: "fa-skull-crossbones" },
 { label: "LTI", value: stats.lostTimeInjuries, color: "dark", icon: "fa-user-injured" },
 { label: "Open CAs", value: stats.openCorrectiveActions, color: "info", icon: "fa-tasks" },
 ].map((s) => (
 <Col key={s.label} xs={6} sm={4} md={2} className="mb-3">
 <Card className={`bg-${s.color} text-white shadow-sm`}>
 <Card.Body className="p-3 d-flex justify-content-between align-items-center">
 <div>
 <p className="mb-0" style={{ fontSize: "0.73rem", opacity: 0.85 }}>
 {s.label}
 </p>
 <h4 className="mb-0 fw-bold">{s.value}</h4>
 </div>
 <i className={`fas ${s.icon} fa-2x opacity-50`} />
 </Card.Body>
 </Card>
 </Col>
 ))}
 </Row>
 )}

 {/* Filters */}
 <Card className="shadow-sm mb-4">
 <Card.Header>
 <h6 className="mb-0">
 <i className="fas fa-filter me-2" /> Report Filters
 </h6>
 </Card.Header>
 <Card.Body>
 <Row className="g-3">
 <Col md={3}>
 <Form.Group>
 <Form.Label className="fs-12 text-muted fw-bold">Project</Form.Label>
 <Form.Select
 value={filters.project}
 onChange={(e) => handleFilterChange("project", e.target.value)}
 >
 <option value="">All Projects</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>
 {p.projectName || p.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label className="fs-12 text-muted fw-bold">Work Order</Form.Label>
 <Form.Select
 value={filters.workOrder}
 onChange={(e) => handleFilterChange("workOrder", e.target.value)}
 >
 <option value="">All WOs</option>
              {filteredWorkOrders.map((wo) => (
                <option key={wo._id} value={wo._id}>
                  {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
                  {!filters.project && (wo.project?.projectName || wo.project?.name) ? ` (${wo.project.projectName || wo.project.name})` : ""}
                </option>
              ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label className="fs-12 text-muted fw-bold">Status</Form.Label>
 <Form.Select
 value={filters.status}
 onChange={(e) => handleFilterChange("status", e.target.value)}
 >
 <option value="">All</option>
 {["Draft", "Submitted", "Under Investigation", "Resolved", "Closed"].map(
 (s) => (
 <option key={s} value={s}>{s}</option>
 )
 )}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label className="fs-12 text-muted fw-bold">Incident Type</Form.Label>
 <Form.Select
 value={filters.incidentType}
 onChange={(e) => handleFilterChange("incidentType", e.target.value)}
 >
 <option value="">All Types</option>
 {INCIDENT_TYPES.map((t) => (
 <option key={t} value={t}>{t}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={1}>
 <Form.Group>
 <Form.Label className="fs-12 text-muted fw-bold">Severity</Form.Label>
 <Form.Select
 value={filters.severity}
 onChange={(e) => handleFilterChange("severity", e.target.value)}
 >
 <option value="">All</option>
 {["Low", "Medium", "High", "Critical"].map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={1}>
 <Form.Group>
 <Form.Label className="fs-12 text-muted fw-bold">From</Form.Label>
 <Form.Control
 type="date"
 value={filters.fromDate}
 onChange={(e) => handleFilterChange("fromDate", e.target.value)}
 />
 </Form.Group>
 </Col>
 <Col md={1}>
 <Form.Group>
 <Form.Label className="fs-12 text-muted fw-bold">To</Form.Label>
 <Form.Control
 type="date"
 value={filters.toDate}
 onChange={(e) => handleFilterChange("toDate", e.target.value)}
 />
 </Form.Group>
 </Col>
 </Row>
 <div className="mt-3 d-flex gap-2">
 <Button variant="primary" onClick={handleGenerate} disabled={loading}>
 {loading ? (
 <>
 <Spinner size="sm" className="me-2" />
 Generating...
 </>
 ) : (
 <>
 <i className="fas fa-chart-bar me-2" /> Generate Report
 </>
 )}
 </Button>
 </div>
 </Card.Body>
 </Card>

 {/* Report Output */}
 {reportData && (
 <div>
 {/* Summary */}
 <Card className="shadow-sm mb-4">
 <Card.Header className="d-flex justify-content-between align-items-center">
 <h6 className="mb-0">
 <i className="fas fa-chart-pie me-2" /> Report Summary
 </h6>
 <small className="text-muted">
 Generated: {new Date(reportData.generatedAt).toLocaleString("en-IN")}
 </small>
 </Card.Header>
 <Card.Body>
 <Row>
 {[
 { label: "Total Records", value: reportData.summary.total, color: "primary" },
 { label: "Critical", value: reportData.summary.critical, color: "danger" },
 { label: "High Severity", value: reportData.summary.high, color: "warning" },
 {
 label: "Lost Time Injuries",
 value: reportData.summary.lostTimeInjuries,
 color: "dark",
 },
 {
 label: "Open Corrective Actions",
 value: reportData.summary.openCorrectiveActions,
 color: "info",
 },
 ].map((s) => (
 <Col key={s.label} xs={6} md className="mb-3">
 <div
 className={`border-start border-${s.color} border-3 ps-3`}
 >
 <div className="text-muted" style={{ fontSize: "0.78rem" }}>
 {s.label}
 </div>
 <h4 className={`mb-0 text-${s.color} fw-bold`}>{s.value}</h4>
 </div>
 </Col>
 ))}
 </Row>
 </Card.Body>
 </Card>

 {/* Incidents Table */}
 <Card className="shadow-sm mb-4">
 <Card.Header>
 <h6 className="mb-0">
 <i className="fas fa-list me-2" /> Incident Records ({reportData.records.length})
 </h6>
 </Card.Header>
 <Card.Body className="p-0">
 {reportData.records.length === 0 ? (
 <div className="text-center py-5 text-muted">No records match the filters.</div>
 ) : (
 <div className="table-responsive">
 <Table size="sm" hover className="align-middle mb-0">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Inc. No.</th>
 <th>Date</th>
 <th>Type</th>
 <th>Location</th>
 <th>Involved Person</th>
 <th>Project</th>
 <th>Severity</th>
 <th>Risk</th>
 <th>Open CAs</th>
 <th>Status</th>
 </tr>
 </thead>
 <tbody>
 {reportData.records.map((rec, i) => {
 const openCAs = (rec.correctiveActions || []).filter((ca) =>
 ["Open", "In Progress", "Overdue"].includes(ca.status)
 ).length;
 return (
 <tr key={rec._id}>
 <td>{i + 1}</td>
 <td>
 <strong className="text-primary">{rec.incidentNumber}</strong>
 </td>
 <td>{fmt(rec.incidentDate)}</td>
 <td>
 <Badge bg="light" text="dark" className="border">
 {rec.incidentType}
 </Badge>
 </td>
 <td>
 <small>
 <i className="fas fa-map-marker-alt me-1 text-muted" />
 {rec.location}
 </small>
 </td>
 <td>{rec.involvedPerson || "—"}</td>
 <td>
 <small>
 {rec.project?.projectName || rec.project?.name || "—"}
 </small>
 </td>
 <td>
 <Badge bg={SEVERITY_COLORS[rec.severity] || "secondary"}>
 {rec.severity}
 </Badge>
 </td>
 <td>
 {rec.riskRating ? (
 <Badge bg={SEVERITY_COLORS[rec.riskRating] || "secondary"}>
 {rec.riskRating}
 </Badge>
 ) : (
 "—"
 )}
 </td>
 <td className="text-center">
 {openCAs > 0 ? (
 <Badge bg="danger">{openCAs}</Badge>
 ) : (
 <Badge bg="success">0</Badge>
 )}
 </td>
 <td>
 <Badge bg={STATUS_COLORS[rec.status] || "secondary"}>
 {rec.status}
 </Badge>
 </td>
 </tr>
 );
 })}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>

 {/* Corrective Actions Summary */}
 {reportData.records.some((r) => r.correctiveActions?.length > 0) && (
 <Card className="shadow-sm mb-4">
 <Card.Header>
 <h6 className="mb-0">
 <i className="fas fa-tasks me-2" /> Corrective Actions Register
 </h6>
 </Card.Header>
 <Card.Body className="p-0">
 <div className="table-responsive">
 <Table size="sm" hover className="align-middle mb-0">
 <thead className="table-light">
 <tr>
 <th>Inc. No.</th>
 <th>Action</th>
 <th>Assigned To</th>
 <th>Due Date</th>
 <th>Completed</th>
 <th>Status</th>
 </tr>
 </thead>
 <tbody>
 {reportData.records.flatMap((rec) =>
 (rec.correctiveActions || []).map((ca, i) => (
 <tr key={`${rec._id}-${i}`}>
 <td>
 <small className="text-primary">{rec.incidentNumber}</small>
 </td>
 <td>{ca.action}</td>
 <td>{ca.assignedTo?.name || "—"}</td>
 <td>{fmt(ca.dueDate)}</td>
 <td>{fmt(ca.completedDate)}</td>
 <td>
 <Badge bg={CA_STATUS_COLORS[ca.status] || "secondary"}>
 {ca.status}
 </Badge>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </Table>
 </div>
 </Card.Body>
 </Card>
 )}
 </div>
 )}
 </>
 );
};

export default SafetyReport;
